import { css, styled } from 'styled-components'

import { mq } from 'styles/mq'
import { typography } from 'styles/typography'
import { focusable } from 'styles/utils'

export const SliderContainer = styled.div`
  --swiper-scrollbar-sides-offset: 0%;
  --swiper-scrollbar-bottom: 0;
  --swiper-scrollbar-drag-bg-color: var(--primary-color);
  --swiper-scrollbar-size: 1px;
  --swiper-scrollbar-bg-color: transparent;
  --max-item-width: 13em;

  position: relative;
  border-block: 1px solid var(--border-color);
  cursor: col-resize;

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    font-size: 1rem;
    border-width: 1em 0.8em 0;
    border-style: solid;
    border-color: var(--primary-color) transparent transparent;
  }

  .swiper {
    margin-block: calc(-1 * (var(--swiper-scrollbar-size) + 1px) / 2);
  }

  ::selection {
    background-color: transparent;
  }
`

export const TimelineContainer = styled.div`
  ${typography.text.sm}
  width: max-content;
  margin-block: 5rem 3rem;

  ${mq.large} {
    margin-block: 10rem 3rem;
  }
`

export const Years = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
`

export const Year = styled.li`
  --padding-top: 1rem;
  --margin-top: 1em;

  ${mq.large} {
    --padding-top: 1em;
    --margin-top: 1rem;
  }
`

export const YearLabel = styled.h3`
  ${typography.text.sm}
  position: relative;
  padding: 0 3rem;
  margin: 0;
  width: var(--max-item-width);
  box-sizing: content-box;
  text-align: center;

  ${Year}:first-child & {
    padding-left: calc(50vw - 5em);
  }

  ${Year} &::before {
    --count: calc(var(--ticks-count) - 0);
    content: '';
    display: block;
    position: absolute;
    inset: calc(-1 * var(--margin)) 50% calc(-1 * var(--margin-top)) -50%;
    opacity: 0.2;
    padding-right: calc(100% / var(--ticks-count) / 2);
    border-right: 1px solid currentColor;
    background: repeating-linear-gradient(
      to right,
      transparent,
      transparent round(nearest, calc(100% / var(--count) - 1px), 1px),
      currentColor round(nearest, calc(100% / var(--count) - 1px), 1px),
      currentColor round(nearest, calc(100% / var(--count)), 1px),
      transparent round(nearest, calc(100% / var(--count)), 1px)
    );
    background-clip: content-box;
  }

  ${Year}:first-child &::before {
    --interval: 0.7rem;

    left: 0;
    right: calc(var(--max-item-width) / 2 + 3rem);
    padding-right: calc(var(--interval) / 2);
    background: repeating-linear-gradient(
      to left,
      currentColor 0,
      currentColor 1px,
      transparent 1px,
      transparent calc(var(--interval) + 1px)
    );
    background-clip: content-box;
  }

  &[data-ticks-count='1']::before {
    border-right: 1px solid currentColor;
    background: none;
  }
`

export const Entries = styled.ul`
  display: flex;
  gap: 4em;
  list-style: none;
  margin: var(--margin-top) 0 0;
  padding: var(--padding-top) 3rem 0;
  text-align: center;
  border-top: 1px solid currentColor;

  ${Year}:first-child & {
    padding-left: calc(50vw - 5em);
  }

  ${Year}:last-child & {
    padding-right: calc(50vw - 5em);
  }
`

export const Entry = styled.li<{ $isActive?: boolean }>`
  ${focusable}
  position: relative;
  max-width: var(--max-item-width);
  transition: color 0.3s;
  -webkit-tap-highlight-color: transparent;

  &:first-child {
    width: var(--max-item-width);
  }

  &::before {
    --mark-height: 1em;
    content: '';
    display: block;
    margin: calc(-1 * (var(--mark-height) / 2 + var(--padding-top))) auto 1em;
    width: min-content;
    height: var(--mark-height);
    border-left: 2px solid currentColor;
  }

  ${mq.medium} {
    &::before {
      margin: 2em auto 0.3em;
    }

    &[data-is-even='true']::before {
      margin-block-start: 0;
    }
  }

  ${props =>
    props.$isActive
      ? css`
          color: var(--primary-color);
        `
      : css`
          cursor: pointer;
        `}
`

export const TextLimit = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`

export const ScrollbarSlider = styled.div`
  height: 3px;
  margin-bottom: -2px;
  background-color: var(--primary-color);
`

export const SectionContainer = styled.div`
  ${mq.smallOnly} {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 3rem);
    height: calc(100svh - 3rem);
  }
`
